import React, { useMemo, useState } from 'react';
import { TopologyData, getMetricColor, TOPOLOGY_LINK_STROKE } from '../TopologyData';
import RouterMatrixGrid from './RouterMatrixGrid';

export type MatrixFilter = 'all' | 'asymmetric';

interface Props {
    data: TopologyData;
    filter?: MatrixFilter;
}

interface CellData {
    forwardMetric: number | null;
    reverseMetric: number | null;
    asymmetric: boolean;
}

const IsisMetricMatrix: React.FC<Props> = ({ data, filter: controlledFilter }) => {
    const [internalFilter, setFilter] = useState<MatrixFilter>('all');
    const filter = controlledFilter ?? internalFilter;

    const { routers, matrix, hasAsymmetry } = useMemo(() => {
        const routerSet = new Set<string>();
        for (const link of data.links) {
            routerSet.add(link.source);
            routerSet.add(link.target);
        }
        const routerList = Array.from(routerSet).sort((a, b) =>
            a.localeCompare(b)
        );

        const mat: Record<string, Record<string, CellData>> = {};
        for (const r of routerList) {
            mat[r] = {};
        }

        let asymFound = false;
        for (const link of data.links) {
            const fwd = link.forwardMetric ?? link.metric;
            const rev = link.reverseMetric ?? link.metric;
            const asym = fwd !== rev;
            if (asym) asymFound = true;

            mat[link.source][link.target] = {
                forwardMetric: fwd,
                reverseMetric: rev,
                asymmetric: asym,
            };
            mat[link.target][link.source] = {
                forwardMetric: rev,
                reverseMetric: fwd,
                asymmetric: asym,
            };
        }

        return { routers: routerList, matrix: mat, hasAsymmetry: asymFound };
    }, [data]);

    const filteredRouters = filter === 'asymmetric'
        ? routers.filter((r) =>
            routers.some((c) => matrix[r][c]?.asymmetric)
        )
        : routers;

    return (
        <div className="flex flex-col gap-3 h-full overflow-auto p-4">
            {!controlledFilter && (
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-semantic-text-primary">
                        IS-IS Metric Symmetry Matrix
                    </h3>
                    <div className="flex gap-1 p-0.5 bg-semantic-bg-secondary rounded-lg">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1 text-xs font-medium rounded-md
                                transition-colors ${
                                    filter === 'all'
                                        ? 'bg-semantic-bg-primary text-semantic-text-primary shadow-sm'
                                        : 'text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            All ({routers.length})
                        </button>
                        <button
                            onClick={() => setFilter('asymmetric')}
                            className={`px-3 py-1 text-xs font-medium rounded-md
                                transition-colors ${
                                    filter === 'asymmetric'
                                        ? 'bg-semantic-bg-primary text-semantic-text-primary shadow-sm'
                                        : 'text-semantic-text-secondary hover:text-semantic-text-primary'
                                }`}
                        >
                            Asymmetric only
                        </button>
                    </div>
                </div>
            )}

            {!hasAsymmetry && filter === 'asymmetric' && (
                <div className="flex items-center justify-center py-8 text-sm text-semantic-text-secondary">
                    No asymmetric metrics detected. All links have symmetric IS-IS costs.
                </div>
            )}

            <RouterMatrixGrid
                ids={filteredRouters}
                renderCell={(row, col) => {
                    const cell = matrix[row]?.[col];
                    if (!cell) {
                        return (
                            <td
                                className="p-1 border
                                    border-semantic-border-secondary
                                    text-center
                                    text-semantic-text-secondary"
                            />
                        );
                    }
                    const metric = cell.forwardMetric;
                    return (
                        <td
                            className="p-1 border text-center
                                font-semibold"
                            style={{
                                borderColor: cell.asymmetric
                                    ? 'var(--color-semantic-text-danger)'
                                    : undefined,
                                borderWidth: cell.asymmetric
                                    ? 2
                                    : undefined,
                                color: metric != null
                                    ? getMetricColor(metric)
                                    : undefined,
                            }}
                            title={
                                cell.asymmetric
                                    ? `${row}->${col}: ${cell.forwardMetric}, ${col}->${row}: ${cell.reverseMetric}`
                                    : `Cost: ${metric}`
                            }
                        >
                            {metric ?? ''}
                        </td>
                    );
                }}
            />

            <div className="flex items-center gap-4 text-xs text-semantic-text-secondary mt-1">
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block w-3 h-3 border-2 rounded-sm"
                        style={{
                            borderColor: TOPOLOGY_LINK_STROKE.danger,
                        }}
                    />
                    {'Asymmetric cost (A->B differs from B->A)'}
                </span>
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{
                            background: TOPOLOGY_LINK_STROKE.success,
                        }}
                    />
                    {'Low cost'}
                </span>
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{
                            background: TOPOLOGY_LINK_STROKE.warning,
                        }}
                    />
                    {'Mid cost'}
                </span>
                <span className="flex items-center gap-1">
                    <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{
                            background: TOPOLOGY_LINK_STROKE.danger,
                        }}
                    />
                    {'High cost'}
                </span>
            </div>
        </div>
    );
};

export default IsisMetricMatrix;
